import React, { Suspense, lazy, useCallback, useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { BookCheck, ClipboardList } from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { hasPermission } from "../../utils/permissionUtils";
import {
  PENDING_APPROVALS,
  CHANGE_REQUEST_PENDING_APPROVALS,
} from "../../constants/permissionKeys";
import Loader from "../../Loader";

const ApproveTickets = lazy(() => import("./ApproveTickets"));
const ChangeRequestPendingApprovals = lazy(
  () => import("../ChangeRequests/ChangeRequestPendingApprovals"),
);

const TAB_TICKETS = "tickets";
const TAB_CHANGE_REQUESTS = "change-requests";

/**
 * Unified pending-approvals shell: tickets (ApproveTickets) and change requests
 * (ChangeRequestPendingApprovals) via tabs. Does not modify those child components.
 */
const PendingApprovalsHub = ({ initialTab }) => {
  const entitlement = useAppSelector((state) => state.userEntitlement?.data);
  const [searchParams, setSearchParams] = useSearchParams();

  const canTickets = hasPermission(entitlement, PENDING_APPROVALS);
  const canChangeRequests = hasPermission(
    entitlement,
    CHANGE_REQUEST_PENDING_APPROVALS,
  );

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (canTickets) {
      tabs.push({
        id: TAB_TICKETS,
        label: "Tickets",
        icon: BookCheck,
      });
    }
    if (canChangeRequests) {
      tabs.push({
        id: TAB_CHANGE_REQUESTS,
        label: "Change Requests",
        icon: ClipboardList,
      });
    }
    return tabs;
  }, [canTickets, canChangeRequests]);

  const tabFromUrl = searchParams.get("tab");
  const resolvedTab = useMemo(() => {
    if (tabFromUrl && availableTabs.some((t) => t.id === tabFromUrl)) {
      return tabFromUrl;
    }
    if (initialTab && availableTabs.some((t) => t.id === initialTab)) {
      return initialTab;
    }
    return availableTabs[0]?.id ?? null;
  }, [tabFromUrl, initialTab, availableTabs]);

  const setActiveTab = useCallback(
    (tabId) => {
      const next = new URLSearchParams(searchParams);
      if (tabId === availableTabs[0]?.id) {
        next.delete("tab");
      } else {
        next.set("tab", tabId);
      }
      setSearchParams(next, { replace: true });
    },
    [availableTabs, searchParams, setSearchParams],
  );

  if (!canTickets && !canChangeRequests) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="max-w-full mx-auto space-y-3">
      <Suspense fallback={<Loader />}>
        {resolvedTab === TAB_TICKETS ? <ApproveTickets /> : null}
        {resolvedTab === TAB_CHANGE_REQUESTS ? (
          <ChangeRequestPendingApprovals />
        ) : null}
      </Suspense>
    </div>
  );
};

export default PendingApprovalsHub;
